import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { inputClass } from "@/lib/formStyles";
import { changeVisitReason, reassignDoctor } from "../../services/queue";
import { QueueEntryApi } from "../../services/queue/types";
import { patientDisplayName } from "../../services/queue/stats";
import { REASON_NOTE_MAX_LENGTH } from "../../services/visitReasons/types";
import useVisitReasonsLookup from "../../hooks/queries/useVisitReasonsLookup";
import { DoctorOption } from "../../hooks/queries/useDoctors";
import VisitReasonPicker from "../VisitReason/VisitReasonPicker";
import ModalUI from "../legacy/Modal";
import { DoctorSelect, QueueModalActions, QueueModalTitle } from "./QueueModalParts";

type EditQueueEntryModalProps = {
  /** Entrée à jour (flux SSE) : les comparaisons se font contre l'état serveur courant. */
  entry: QueueEntryApi;
  doctors: DoctorOption[];
  onClose: () => void;
};

/**
 * Correction d'une entrée par la secrétaire : motif, précision et médecin.
 * N'appelle que les endpoints dont la valeur a changé. Le flux SSE met aussi à
 * jour la vue du médecin. La liste est rafraîchie même si seule la première
 * modification a abouti, pour ne jamais afficher un motif périmé.
 */
const EditQueueEntryModal: React.FC<EditQueueEntryModalProps> = ({ entry, doctors, onClose }) => {
  const queryClient = useQueryClient();
  const { data: visitReasons = [] } = useVisitReasonsLookup();
  const [visitReasonId, setVisitReasonId] = useState<number | null>(entry.visitReasonId);
  const [reasonNote, setReasonNote] = useState(entry.reasonNote ?? "");
  const [doctorId, setDoctorId] = useState(String(entry.assignedDoctorId ?? ""));

  const normalizedNote = reasonNote.trim() || null;
  const reasonChanged = visitReasonId !== entry.visitReasonId || normalizedNote !== (entry.reasonNote ?? null);
  const doctorChanged = doctorId !== String(entry.assignedDoctorId ?? "");

  const { mutate: save, isLoading } = useMutation(
    async () => {
      if (reasonChanged && visitReasonId != null) {
        const result: any = await changeVisitReason(entry.id, visitReasonId, normalizedNote);
        if (!result?.ok) return result;
      }
      if (doctorChanged) {
        return reassignDoctor(entry.id, Number(doctorId));
      }
      return { ok: true };
    },
    {
      onSettled: () => queryClient.invalidateQueries(["queue-today-list"]),
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Entrée modifiée");
        onClose();
      },
    }
  );

  const canSubmit = (reasonChanged || doctorChanged) && visitReasonId != null && Boolean(doctorId) && !isLoading;

  return (
    <ModalUI
      isOpen
      onClose={() => {
        if (!isLoading) onClose();
      }}
      styles={{ width: "min(580px, 92vw)", padding: 0 }}
    >
      <div className="flex flex-col">
        <div className="space-y-5 px-6 pt-6">
          <QueueModalTitle eyebrow="Modifier l'entrée" title={patientDisplayName(entry)} />

          <div>
            <span className="mb-2 block text-sm font-medium text-slate-700">Motif de visite</span>
            <VisitReasonPicker reasons={visitReasons} value={visitReasonId} onChange={setVisitReasonId} disabled={isLoading} />
          </div>

          <label className="block text-sm font-medium text-slate-700">
            Précision <span className="font-normal text-slate-400">(facultatif)</span>
            <input
              type="text"
              className={inputClass}
              value={reasonNote}
              maxLength={REASON_NOTE_MAX_LENGTH}
              onChange={(event) => setReasonNote(event.target.value)}
            />
          </label>

          <DoctorSelect doctors={doctors} value={doctorId} onChange={setDoctorId} disabled={isLoading} />
        </div>

        <QueueModalActions
          onCancel={onClose}
          onConfirm={() => save()}
          confirmLabel="Enregistrer"
          confirmIcon="bx-check"
          confirmDisabled={!canSubmit}
          isLoading={isLoading}
        />
      </div>
    </ModalUI>
  );
};

export default EditQueueEntryModal;
